import type {
  AcademicCalendar,
  AssignResultTemplateToProgrammesInput,
  HistoricalSourceMapInput,
  ProgrammeTemplateAssignmentResult,
  ResultTemplate,
  ResultTemplateColumn,
  SetProgrammesResultTemplateDefaultMutationVariables,
} from '@/types/operations';

/**
 * Column types that read data from an earlier semester. A template with any of
 * them needs a per-semester source map, so it can only be assigned to one
 * semester at a time and can never be a programme's all-semesters default.
 * Mirrors ProgrammeTemplateAssignmentService::HISTORICAL_COLUMN_TYPES on the backend.
 */
export const HISTORICAL_COLUMN_TYPES = ['HISTORICAL', 'HISTORICAL_LOOKUP', 'PREVIOUS_TERM'] as const;

/** Where the template applies: every semester (programme default) or chosen semesters. */
export const APPLY_MODE = {
  ALL: 'ALL',
  SPECIFIC: 'SPECIFIC',
} as const;

export type ApplyMode = typeof APPLY_MODE[keyof typeof APPLY_MODE];

export type TemplateColumnLike = Pick<ResultTemplateColumn, 'key' | 'label' | 'type'>
  & Partial<Pick<ResultTemplateColumn, 'calculation_arguments'>>;

export const NO_DEFAULT_TEMPLATE_LABEL = 'No default';

const plural = (count: number, singular: string, pluralForm = `${singular}s`) =>
  `${count} ${count === 1 ? singular : pluralForm}`;

export function isHistoricalColumnType(type: string | null | undefined): boolean {
  return !!type && (HISTORICAL_COLUMN_TYPES as readonly string[]).includes(type.toUpperCase());
}

export function getHistoricalColumns<T extends Pick<ResultTemplateColumn, 'type'>>(
  columns: ReadonlyArray<T | null | undefined> | null | undefined,
): T[] {
  return (columns ?? []).filter((col): col is T => !!col && isHistoricalColumnType(col.type));
}

/** "<session> – <term>", with neutral fallbacks when either side is missing. */
export function formatCalendarLabel(
  calendar: Pick<AcademicCalendar, 'session' | 'termStructure'> | null | undefined,
): string {
  return `${calendar?.session?.name || 'Session'} – ${calendar?.termStructure?.name || 'Term'}`;
}

export function programmeDefaultTemplateLabel(
  programme: { default_result_template?: Pick<ResultTemplate, 'name'> | null } | null | undefined,
): string {
  return programme?.default_result_template?.name || NO_DEFAULT_TEMPLATE_LABEL;
}

/** Source map entries for the historical columns the user has mapped. */
export function buildHistoricalSourceMap(
  columns: ReadonlyArray<Pick<ResultTemplateColumn, 'key'>>,
  mapping: Record<string, string | null | undefined>,
): HistoricalSourceMapInput[] {
  return columns
    .filter((col) => !!mapping[col.key])
    .map((col) => ({ key: col.key, academic_calendar_id: mapping[col.key] as string }));
}

export function buildSetDefaultVariables(
  programmeIds: readonly string[],
  templateId: string,
): SetProgrammesResultTemplateDefaultMutationVariables {
  return { programme_ids: [...programmeIds], result_template_id: templateId };
}

export function buildAssignProgrammesInput(params: {
  programmeIds: readonly string[];
  calendarIds: readonly string[];
  templateId: string;
  historicalSourceMap?: HistoricalSourceMapInput[];
  overwrite?: boolean;
}): AssignResultTemplateToProgrammesInput {
  const input: AssignResultTemplateToProgrammesInput = {
    programme_ids: [...params.programmeIds],
    academic_calendar_ids: [...params.calendarIds],
    result_template_id: params.templateId,
    overwrite: params.overwrite ?? false,
  };
  if (params.historicalSourceMap && params.historicalSourceMap.length > 0) {
    input.historical_source_map = params.historicalSourceMap;
  }
  return input;
}

/** The first reason the form cannot be submitted yet, or null when it is ready. */
export function assignmentValidationMessage(params: {
  mode: ApplyMode;
  programmeIds: readonly string[];
  calendarIds: readonly string[];
  historicalColumns: ReadonlyArray<Pick<ResultTemplateColumn, 'key'>>;
  historicalMapping: Record<string, string | null | undefined>;
}): string | null {
  if (params.programmeIds.length === 0) return 'Select at least one programme.';
  if (params.mode === APPLY_MODE.ALL) return null;
  if (params.calendarIds.length === 0) return 'Select at least one semester.';
  if (params.historicalColumns.some((col) => !params.historicalMapping[col.key])) {
    return 'Choose a source semester for every historical column.';
  }
  return null;
}

export function setDefaultSummaryMessage(programmeCount: number): string {
  return `Set as the default template for ${plural(programmeCount, 'programme')}`;
}

/** e.g. "Assigned to 6 programme-semesters · 2 already used this template · 1 replaced". */
export function assignmentSummaryMessage(
  result: Pick<ProgrammeTemplateAssignmentResult, 'assigned' | 'unchanged' | 'overwritten'>,
): string {
  const parts: string[] = [];
  if (result.assigned > 0) parts.push(`Assigned to ${plural(result.assigned, 'programme-semester')}`);
  if (result.unchanged > 0) {
    parts.push(result.assigned > 0
      ? `${result.unchanged} already used this template`
      : `${plural(result.unchanged, 'programme-semester')} already used this template`);
  }
  if (result.overwritten > 0) parts.push(`${result.overwritten} replaced`);
  return parts.length > 0 ? parts.join(' · ') : 'Nothing to change';
}

export function submitButtonLabel(mode: ApplyMode, programmeCount: number, calendarCount: number): string {
  if (mode === APPLY_MODE.ALL) {
    return programmeCount > 0 ? `Set default for ${plural(programmeCount, 'programme')}` : 'Set default';
  }
  const pairs = programmeCount * calendarCount;
  return pairs > 0 ? `Assign to ${plural(pairs, 'programme-semester')}` : 'Assign template';
}

export function replaceButtonLabel(conflictCount: number): string {
  return `Replace ${plural(conflictCount, 'assignment')}`;
}

/** "B.Sc Computer Science (CSC)", or just the name when the code is missing. */
export function programmeChipLabel(programme: { program_name: string; prog_code?: string | null }): string {
  return programme.prog_code ? `${programme.program_name} (${programme.prog_code})` : programme.program_name;
}

type CalendarLike = Pick<AcademicCalendar, 'id' | 'session' | 'termStructure'> & { is_active?: boolean | null };

/**
 * Semester picker options, newest session first ("2026/2027" before
 * "2024/2025", compared numerically) and terms in order within a session; the
 * active semester is tagged "(current)". Calendars arrive in database id order,
 * which put a later-created older session after newer ones.
 */
export function buildCalendarOptions(calendars: ReadonlyArray<CalendarLike | null | undefined>): Array<{ id: string; label: string }> {
  const collator = new Intl.Collator(undefined, { numeric: true, sensitivity: 'base' });

  return calendars
    .filter((calendar): calendar is CalendarLike => !!calendar)
    .slice()
    .sort((a, b) => collator.compare(b.session?.name ?? '', a.session?.name ?? '')
      || collator.compare(a.termStructure?.name ?? '', b.termStructure?.name ?? ''))
    .map((calendar) => ({
      id: calendar.id,
      label: `${formatCalendarLabel(calendar)}${calendar.is_active ? ' (current)' : ''}`,
    }));
}
